/**
 * MCP Appointments API Queries
 * React Query hooks for fetching resources and appointments
 *
 * Gateway mapping: /mcp-appointments/* → mcp-appointments service
 */

import { queryOptions, useQuery } from '@tanstack/react-query';
import { apiGet } from '@/api/_shared/http';
import { useMemo } from 'react';
import { useRoleQueryGate } from '@/api/_shared/roleGate';
import { withAdminScopeParams } from '@/utils/apiRole';
import {
  roleResourcesBase,
  roleAppointmentsBase,
  roleServicesBase,
  roleServiceRolesBase,
  customerRescheduleRequestsBase,
  roleRescheduleRequestsBase
} from './paths';
import type {
  ListResourcesResponse,
  GetResourceResponse,
  ResourcePolicyResponse,
  ResourceAvailabilityResponse,
  ResourceExceptionsResponse,
  ListResourcesParams,
  ListClientResourcesParams,
  ListAppointmentsResponse,
  ListAppointmentsParams,
  ListClientAppointmentsResponse,
  ListClientAppointmentsParams,
  AppointmentStatsParams,
  ClientAppointmentStatsParams,
  AppointmentStatsResponse,
  ListAppointmentsWithExternalResponse,
  AppointmentWithExternalConflict,
  FindAvailableSlotsParams,
  FindAvailableSlotsResponse,
  ListCalendarImportFeedsResponse,
  CalendarExportFeed,
  ListServicesResponse,
  GetServiceResponse,
  ListServicesParams,
  ListClientServicesParams,
  FindServiceAvailableSlotsParams,
  FindServiceAvailableSlotsResponse,
  ListServiceRolesResponse,
  GetServiceRoleResponse,
  ListServiceRolesParams,
  ListClientServiceRolesParams,
  ListCustomerRescheduleRequestsParams,
  ListCustomerRescheduleRequestsResponse,
  ListClientChangeRequestsParams,
  ListClientRescheduleRequestsResponse,
  CalDavFeed
} from './interfaces';
import { calendarExportPublicBaseUrl } from '@/utils/calendarIcs';
import { apiGetOrNullOn404 } from '@/api/_shared/http';
import {
  appointmentKeys,
  resourceKeys,
  serviceKeys,
  serviceRoleKeys
} from './keys';
import { STALE_30S, STALE_2M, STALE_5M, RETRY_ONCE } from '@/api/_shared/queryDefaults';

//
// Resource Queries
// -------------------------------------------------

/**
 * Fetch list of resources (role-aware).
 * - ADMIN: GET /api/resources with clientId query param (requires selectedClientId)
 * - CLIENT: GET /api/client/resources
 *
 * @param params - Optional filters (companyId, name, type, languageCode)
 * @param enabled - Whether the query should be enabled
 */
export const useListResourcesQuery = (
  params?: ListResourcesParams | ListClientResourcesParams,
  enabled: boolean = true
) => {
  const { ready, role, queryKey: roleKeySegments, selectedClientId, selectedCompanyId, effectiveCompanyId } =
    useRoleQueryGate();

  const config = useMemo(() => {
    const endpointBase = roleResourcesBase(role);
    const typedParams = withAdminScopeParams(
      role,
      params as ListResourcesParams | undefined,
      { clientId: selectedClientId, companyId: effectiveCompanyId }
    );
    return {
      endpoint: endpointBase,
      queryKey: resourceKeys.list(roleKeySegments, typedParams),
      typedParams
    } as const;
  }, [role, roleKeySegments, selectedClientId, effectiveCompanyId, params]);

  return useQuery({
    queryKey: config.queryKey,
    queryFn: async (): Promise<ListResourcesResponse> => {
      return apiGet<ListResourcesResponse>(config.endpoint, { params: config.typedParams });
    },
    enabled: enabled && ready,
    staleTime: STALE_2M, // 2 minutes
    retry: RETRY_ONCE
  });
};

/**
 * Fetch a single resource by ID with full details (role-aware).
 * Returns the resource together with its policy, availability, and exceptions.
 *
 * @param resourceId - The resource ID to fetch
 * @param enabled - Whether the query should be enabled
 */
export const useGetResourceQuery = (resourceId: string, enabled: boolean = true) => {
  const { ready, role, roleKey } = useRoleQueryGate();
  const endpoint = `${roleResourcesBase(role)}/${resourceId}`;

  return useQuery({
    queryKey: resourceKeys.detail(roleKey, resourceId),
    queryFn: async (): Promise<GetResourceResponse> => {
      return apiGet<GetResourceResponse>(endpoint);
    },
    enabled: enabled && !!resourceId && ready,
    staleTime: STALE_2M, // 2 minutes
    retry: RETRY_ONCE
  });
};

/**
 * Fetch the booking policy for a resource (role-aware).
 * Returns `null` when no policy exists yet (404).
 *
 * @param resourceId - The resource whose policy to fetch
 * @param enabled - Whether the query should be enabled
 */
export const useGetResourcePolicyQuery = (resourceId: string, enabled: boolean = true) => {
  const { ready, role, roleKey } = useRoleQueryGate();
  const endpoint = `${roleResourcesBase(role)}/${resourceId}/policy`;

  return useQuery<ResourcePolicyResponse | null>({
    queryKey: resourceKeys.policy(roleKey, resourceId),
    queryFn: async (): Promise<ResourcePolicyResponse | null> =>
      apiGetOrNullOn404<ResourcePolicyResponse>(endpoint),
    enabled: enabled && !!resourceId && ready,
    staleTime: STALE_5M, // 5 minutes
    retry: RETRY_ONCE
  });
};

/**
 * Fetch weekly availability rules for a resource on a specific weekday (role-aware).
 *
 * @param resourceId - The resource whose availability to fetch
 * @param weekday - ISO weekday abbreviation (MON–SUN)
 * @param enabled - Whether the query should be enabled
 */
export const useGetResourceAvailabilityQuery = (
  resourceId: string,
  weekday: string,
  enabled: boolean = true
) => {
  const { ready, role, roleKey } = useRoleQueryGate();
  const endpoint = `${roleResourcesBase(role)}/${resourceId}/availability/${weekday}`;

  return useQuery({
    queryKey: resourceKeys.availability(roleKey, resourceId, weekday),
    queryFn: async (): Promise<ResourceAvailabilityResponse> => {
      return apiGet<ResourceAvailabilityResponse>(endpoint);
    },
    enabled: enabled && !!resourceId && !!weekday && ready,
    staleTime: STALE_5M, // 5 minutes
    retry: RETRY_ONCE
  });
};

/**
 * Fetch exceptions for a resource within an optional date range (role-aware).
 *
 * @param resourceId - The resource whose exceptions to fetch
 * @param start - Optional ISO-8601 range start
 * @param end - Optional ISO-8601 range end
 * @param enabled - Whether the query should be enabled
 */
export const useGetResourceExceptionsQuery = (
  resourceId: string,
  start?: string,
  end?: string,
  enabled: boolean = true
) => {
  const { ready, role, roleKey } = useRoleQueryGate();
  const base = roleResourcesBase(role);

  return useQuery({
    queryKey: resourceKeys.exceptions(roleKey, resourceId, { start, end }),
    queryFn: async (): Promise<ResourceExceptionsResponse> => {
      const params: { start?: string; end?: string } = {};
      if (start) params.start = start;
      if (end) params.end = end;

      return apiGet<ResourceExceptionsResponse>(`${base}/${resourceId}/exceptions`, { params });
    },
    enabled: enabled && !!resourceId && ready,
    staleTime: STALE_2M, // 2 minutes
    retry: RETRY_ONCE
  });
};

/**
 * Fetch available appointment slots for a resource within a time range (role-aware).
 * - ADMIN: GET /api/resources/:resourceId/available-slots
 * - CLIENT: GET /api/client/resources/:resourceId/available-slots
 *
 * @param resourceId - The resource whose slots to fetch
 * @param params - Search window and optional duration/maxSlots
 * @param enabled - Whether the query should be enabled
 */
export const useFindAvailableSlotsQuery = (
  resourceId: string,
  params: FindAvailableSlotsParams | null | undefined,
  enabled: boolean = true
) => {
  const { ready, role, roleKey } = useRoleQueryGate();
  const endpoint = `${roleResourcesBase(role)}/${resourceId}/available-slots`;

  return useQuery({
    queryKey: resourceKeys.availableSlots(roleKey, resourceId, params),
    queryFn: async (): Promise<FindAvailableSlotsResponse> => {
      return apiGet<FindAvailableSlotsResponse>(endpoint, { params });
    },
    enabled: enabled && !!resourceId && !!params?.startTime && !!params?.endTime && ready,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
    retry: RETRY_ONCE
  });
};

/**
 * List ICS import feeds configured for a resource (role-aware).
 *
 * @param resourceId - The resource whose import feeds to fetch
 * @param enabled - Whether the query should be enabled
 */
export const useListCalendarImportFeedsQuery = (resourceId: string, enabled: boolean = true) => {
  const { ready, role, roleKey } = useRoleQueryGate();

  return useQuery({
    ...calendarImportFeedsQueryOptions(role, roleKey, resourceId),
    enabled: enabled && !!resourceId && ready
  });
};

/**
 * Query options for one resource's ICS import feeds; shared by the single-resource hook and
 * `useCalendarImportFeedsByResources`, which fetches several resources in parallel.
 * @param role - Caller role, used to pick the role-aware endpoint.
 * @param roleKey - First role segment of the cache key (`roleKey` from `useRoleQueryGate`).
 */
export const calendarImportFeedsQueryOptions = (
  role: string | undefined,
  roleKey: string | undefined,
  resourceId: string
) =>
  queryOptions({
    queryKey: resourceKeys.calendarImportFeeds(roleKey, resourceId),
    queryFn: (): Promise<ListCalendarImportFeedsResponse> =>
      apiGet<ListCalendarImportFeedsResponse>(`${roleResourcesBase(role)}/${resourceId}/calendar/import-ics-feeds`),
    staleTime: STALE_30S,
    retry: RETRY_ONCE
  });

export const useGetCalDavFeedQuery = (resourceId: string, enabled: boolean = true) => {
  const { ready, role, roleKey } = useRoleQueryGate();
  const endpoint = `${roleResourcesBase(role)}/${resourceId}/calendar/caldav/feed`;
  return useQuery<CalDavFeed | null>({
    queryKey: resourceKeys.calDavFeed(roleKey, resourceId),
    queryFn: () => apiGetOrNullOn404<CalDavFeed>(endpoint),
    enabled: enabled && !!resourceId && ready,
    staleTime: STALE_30S,
    refetchInterval: 30_000,
    retry: RETRY_ONCE
  });
};


/**
 * Fetch the outbound ICS export feed for a resource (role-aware).
 * Returns null when no export feed exists yet (404).
 *
 * @param resourceId - The resource whose export feed to fetch
 * @param enabled - Whether the query should be enabled
 */
export const useGetCalendarExportFeedQuery = (resourceId: string, enabled: boolean = true) => {
  const { ready, role, roleKey } = useRoleQueryGate();
  const endpoint = `${roleResourcesBase(role)}/${resourceId}/calendar/export-ics-feed`;
  const baseUrl = calendarExportPublicBaseUrl();

  return useQuery<CalendarExportFeed | null>({
    queryKey: resourceKeys.calendarExportFeed(roleKey, resourceId, baseUrl),
    queryFn: async (): Promise<CalendarExportFeed | null> =>
      apiGetOrNullOn404<CalendarExportFeed>(endpoint, { params: { baseUrl } }),
    enabled: enabled && !!resourceId && ready,
    staleTime: STALE_30S,
    retry: RETRY_ONCE
  });
};

//
// Service Queries
// -------------------------------------------------

/**
 * Fetch list of services (role-aware).
 * - ADMIN: GET /api/services with clientId query param
 * - CLIENT: GET /api/client/services
 *
 * @param params - Optional filters (companyId, name, isActive)
 * @param enabled - Whether the query should be enabled
 */
export const useListServicesQuery = (
  params?: ListServicesParams | ListClientServicesParams,
  enabled: boolean = true
) => {
  const { ready, role, queryKey: roleKeySegments, selectedClientId, effectiveCompanyId } = useRoleQueryGate();

  const config = useMemo(() => {
    const endpoint = roleServicesBase(role);
    const typedParams = withAdminScopeParams(
      role,
      params as ListServicesParams | undefined,
      { clientId: selectedClientId, companyId: effectiveCompanyId }
    );
    return {
      endpoint,
      queryKey: serviceKeys.list(roleKeySegments, typedParams),
      typedParams
    } as const;
  }, [role, roleKeySegments, selectedClientId, effectiveCompanyId, params]);

  return useQuery({
    queryKey: config.queryKey,
    queryFn: async (): Promise<ListServicesResponse> => {
      return apiGet<ListServicesResponse>(config.endpoint, { params: config.typedParams });
    },
    enabled: enabled && ready,
    staleTime: STALE_2M, // 2 minutes
    retry: RETRY_ONCE
  });
};

/**
 * Fetch a single service by ID with full details (role-aware).
 * Returns the service together with availability rules, requirements, and linked resources.
 *
 * @param serviceId - The service ID to fetch
 * @param enabled - Whether the query should be enabled
 */
export const useGetServiceQuery = (serviceId: string, enabled: boolean = true) => {
  const { ready, role, roleKey } = useRoleQueryGate();
  const endpoint = `${roleServicesBase(role)}/${serviceId}`;

  return useQuery({
    queryKey: serviceKeys.detail(roleKey, serviceId),
    queryFn: async (): Promise<GetServiceResponse> => {
      return apiGet<GetServiceResponse>(endpoint);
    },
    enabled: enabled && !!serviceId && ready,
    staleTime: STALE_2M, // 2 minutes
    retry: RETRY_ONCE
  });
};

/**
 * Fetch available appointment slots for a service within a time range (role-aware).
 * - ADMIN: GET /api/services/:serviceId/available-slots
 * - CLIENT: GET /api/client/services/:serviceId/available-slots
 *
 * @param serviceId - The service whose slots to fetch
 * @param params - Search window and optional slot options
 * @param enabled - Whether the query should be enabled
 */
export const useFindServiceAvailableSlotsQuery = (
  serviceId: string,
  params: FindServiceAvailableSlotsParams | null | undefined,
  enabled: boolean = true
) => {
  const { ready, role, roleKey } = useRoleQueryGate();
  const endpoint = `${roleServicesBase(role)}/${serviceId}/available-slots`;

  return useQuery({
    queryKey: serviceKeys.availableSlots(roleKey, serviceId, params),
    queryFn: async (): Promise<FindServiceAvailableSlotsResponse> => {
      return apiGet<FindServiceAvailableSlotsResponse>(endpoint, { params });
    },
    enabled: enabled && !!serviceId && !!params?.startTime && !!params?.endTime && ready,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
    retry: RETRY_ONCE
  });
};

//
// Service Role Queries
// -------------------------------------------------

/**
 * Fetch list of service roles (role-aware).
 * - ADMIN: GET /api/service-roles with clientId query param
 * - CLIENT: GET /api/client/service-roles
 *
 * @param params - Optional filters (id, companyId, key, name)
 * @param enabled - Whether the query should be enabled
 */
export const useListServiceRolesQuery = (
  params?: ListServiceRolesParams | ListClientServiceRolesParams,
  enabled: boolean = true
) => {
  const { ready, role, queryKey: roleKeySegments, selectedClientId, effectiveCompanyId } = useRoleQueryGate();

  const config = useMemo(() => {
    const endpoint = roleServiceRolesBase(role);
    const typedParams = withAdminScopeParams(
      role,
      params as ListServiceRolesParams | undefined,
      { clientId: selectedClientId, companyId: effectiveCompanyId }
    );
    return {
      endpoint,
      queryKey: serviceRoleKeys.list(roleKeySegments, typedParams),
      typedParams
    } as const;
  }, [role, roleKeySegments, selectedClientId, effectiveCompanyId, params]);

  return useQuery({
    queryKey: config.queryKey,
    queryFn: async (): Promise<ListServiceRolesResponse> => {
      return apiGet<ListServiceRolesResponse>(config.endpoint, { params: config.typedParams });
    },
    enabled: enabled && ready,
    staleTime: STALE_2M, // 2 minutes
    retry: RETRY_ONCE
  });
};

/**
 * Fetch a single service role by ID (role-aware).
 *
 * @param roleId - The service role ID to fetch
 * @param enabled - Whether the query should be enabled
 */
export const useGetServiceRoleQuery = (roleId: string, enabled: boolean = true) => {
  const { ready, role, roleKey } = useRoleQueryGate();
  const endpoint = `${roleServiceRolesBase(role)}/${roleId}`;

  return useQuery({
    queryKey: serviceRoleKeys.detail(roleKey, roleId),
    queryFn: async (): Promise<GetServiceRoleResponse> => {
      return apiGet<GetServiceRoleResponse>(endpoint);
    },
    enabled: enabled && !!roleId && ready,
    staleTime: STALE_2M, // 2 minutes
    retry: RETRY_ONCE
  });
};

//
// Appointment Queries
// -------------------------------------------------

/**
 * Stable serialization for React Query keys derived from list/stats params.
 *
 * @param params - Query params object to serialize
 * @returns Sorted param entries, or undefined when empty
 */
export function serializeAppointmentQueryParams(
  params?: object | null
): Record<string, unknown> | undefined {
  if (!params) {
    return undefined;
  }

  const entries = Object.entries(params as Record<string, unknown>).filter(
    ([, value]) => value !== undefined && value !== null
  );
  if (entries.length === 0) {
    return undefined;
  }

  entries.sort(([a], [b]) => a.localeCompare(b));
  return Object.fromEntries(entries);
}

/**
 * Fetch aggregate appointment statistics (role-aware).
 * - ADMIN: GET /api/appointments/stats with clientId query param
 * - CLIENT: GET /api/client/appointments/stats
 *
 * @param params - Optional filters for stats aggregation
 * @param enabled - Whether the query should be enabled
 * @param refetchInterval - Polling interval in ms for auto-refresh (default: false/disabled)
 */
export const useAppointmentStatsQuery = (
  params?: AppointmentStatsParams | ClientAppointmentStatsParams,
  enabled: boolean = true,
  refetchInterval?: number | false
) => {
  const { ready, role, queryKey: roleKeySegments, selectedClientId, effectiveCompanyId } = useRoleQueryGate();

  const config = useMemo(() => {
    const endpoint = `${roleAppointmentsBase(role)}/stats`;
    const typedParams = withAdminScopeParams(
      role,
      params as AppointmentStatsParams | undefined,
      { clientId: selectedClientId, companyId: effectiveCompanyId }
    );
    return {
      endpoint,
      queryKey: appointmentKeys.stats(roleKeySegments, serializeAppointmentQueryParams(typedParams)),
      typedParams
    } as const;
  }, [role, roleKeySegments, selectedClientId, effectiveCompanyId, params]);

  return useQuery({
    queryKey: config.queryKey,
    queryFn: async (): Promise<AppointmentStatsResponse> => {
      return apiGet<AppointmentStatsResponse>(config.endpoint, { params: config.typedParams });
    },
    enabled: enabled && ready,
    staleTime: STALE_30S,
    retry: RETRY_ONCE,
    refetchInterval: refetchInterval !== undefined ? refetchInterval : false
  });
};

/**
 * Fetch list of appointments (role-aware).
 * - ADMIN: GET /api/appointments with clientId query param
 * - CLIENT: GET /api/client/appointments
 *
 * SHADOW appointments are automatically filtered out from the response.
 *
 * @param params - Optional filters (companyId, resourceId, status, userId, startDate, endDate)
 * @param enabled - Whether the query should be enabled
 * @param refetchInterval - Interval in ms for auto-refresh (default: false/disabled)
 */
export const useListAppointmentsQuery = (
  params?: ListAppointmentsParams | ListClientAppointmentsParams,
  enabled: boolean = true,
  refetchInterval?: number | false
) => {
  const { ready, role, queryKey: roleKeySegments, selectedClientId, effectiveCompanyId } = useRoleQueryGate();

  const config = useMemo(() => {
    const endpoint = roleAppointmentsBase(role);
    const typedParams = withAdminScopeParams(
      role,
      params as ListAppointmentsParams | undefined,
      { clientId: selectedClientId, companyId: effectiveCompanyId }
    );
    return {
      endpoint,
      queryKey: appointmentKeys.list(roleKeySegments, serializeAppointmentQueryParams(typedParams)),
      typedParams
    } as const;
  }, [role, roleKeySegments, selectedClientId, effectiveCompanyId, params]);

  return useQuery({
    queryKey: config.queryKey,
    queryFn: async () => {
      const data = await apiGet<ListAppointmentsResponse | ListClientAppointmentsResponse>(
        config.endpoint,
        { params: config.typedParams }
      );

      // Filter out SHADOW appointments — internal/system entries not meant for UI
      const filteredData = Array.isArray(data)
        ? data.filter((appointment) => appointment.status !== 'SHADOW')
        : data;

      return filteredData as ListAppointmentsResponse | ListClientAppointmentsResponse;
    },
    enabled: enabled && ready,
    staleTime: STALE_2M, // 2 minutes
    retry: RETRY_ONCE,
    refetchInterval: refetchInterval !== undefined ? refetchInterval : false
  });
};

/**
 * Fetch appointments with imported ICS blockers and conflict metadata (role-aware).
 * Requires includeExternalAppointments and includeConflictDetails query flags on the API.
 *
 * @param params - Optional list filters
 * @param enabled - Whether the query should be enabled
 * @param refetchInterval - Interval in ms for auto-refresh (default: false/disabled)
 */
export const useListAppointmentsWithExternalQuery = (
  params?: ListAppointmentsParams | ListClientAppointmentsParams,
  enabled: boolean = true,
  refetchInterval?: number | false
) => {
  const { ready, role, queryKey: roleKeySegments, selectedClientId, effectiveCompanyId } = useRoleQueryGate();

  const config = useMemo(() => {
    const endpoint = roleAppointmentsBase(role);
    const typedParams = withAdminScopeParams(
      role,
      params as ListAppointmentsParams | undefined,
      { clientId: selectedClientId, companyId: effectiveCompanyId }
    );
    const requestParams = {
      ...typedParams,
      includeExternalAppointments: true,
      includeConflictDetails: true
    };
    return {
      endpoint,
      queryKey: appointmentKeys.listExternal(roleKeySegments, serializeAppointmentQueryParams(requestParams)),
      typedParams: requestParams
    } as const;
  }, [role, roleKeySegments, selectedClientId, effectiveCompanyId, params]);

  return useQuery({
    queryKey: config.queryKey,
    queryFn: async () => {
      const data = await apiGet<ListAppointmentsWithExternalResponse>(config.endpoint, {
        params: config.typedParams
      });

      const appointments = (data.appointments ?? []).filter(
        (appointment) => appointment.status !== 'SHADOW'
      ) as AppointmentWithExternalConflict[];

      return {
        appointments,
        externalAppointments: data.externalAppointments ?? [],
        totalConflictCount: data.totalConflictCount ?? 0
      } satisfies ListAppointmentsWithExternalResponse;
    },
    enabled: enabled && ready,
    staleTime: STALE_2M,
    retry: RETRY_ONCE,
    refetchInterval: refetchInterval !== undefined ? refetchInterval : false
  });
};

/**
 * List the authenticated customer's appointment reschedule requests.
 * GET /mcp-appointments/api/customer/appointment-reschedule-requests
 */
export const useListCustomerRescheduleRequestsQuery = (
  params?: ListCustomerRescheduleRequestsParams,
  enabled: boolean = true
) => {
  const { ready, role } = useRoleQueryGate();

  return useQuery({
    queryKey: appointmentKeys.rescheduleRequests(params ?? {}),
    queryFn: async (): Promise<ListCustomerRescheduleRequestsResponse> => {
      return apiGet<ListCustomerRescheduleRequestsResponse>(customerRescheduleRequestsBase(), {
        params
      });
    },
    enabled: enabled && ready && role === 'CUSTOMER',
    staleTime: STALE_30S,
    retry: RETRY_ONCE
  });
};

export const useListClientRescheduleRequestsQuery = (
  params?: ListClientChangeRequestsParams,
  enabled: boolean = true,
  refetchInterval?: number | false
) => {
  const { ready, role, queryKey: roleKeySegments, selectedClientId, effectiveCompanyId } = useRoleQueryGate();

  const config = useMemo(() => {
    const endpoint = roleRescheduleRequestsBase(role);
    const typedParams = withAdminScopeParams(role, params, {
      clientId: selectedClientId,
      companyId: effectiveCompanyId
    });
    return {
      endpoint,
      queryKey: appointmentKeys.clientRescheduleRequests(roleKeySegments, typedParams ?? {}),
      typedParams
    } as const;
  }, [role, roleKeySegments, selectedClientId, effectiveCompanyId, params]);

  return useQuery({
    queryKey: config.queryKey,
    queryFn: async (): Promise<ListClientRescheduleRequestsResponse> => {
      return apiGet<ListClientRescheduleRequestsResponse>(config.endpoint, { params: config.typedParams });
    },
    enabled: enabled && ready && role !== 'CUSTOMER',
    staleTime: STALE_30S,
    retry: RETRY_ONCE,
    refetchInterval: refetchInterval !== undefined ? refetchInterval : false
  });
};
